import React, { useState } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_611a485b-e4e6-4486-b989-98a526add30a/artifacts/7n1cgjv3_ProCloud-Logo-Stacked-Primary.svg";

export default function Login() {
  const { user, login, error } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const nav = useNavigate();

  if (user) return <Navigate to="/dashboard" replace />;

  const onSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    const ok = await login(email, password);
    setSubmitting(false);
    if (ok) nav("/dashboard");
  };

  return (
    <div className="min-h-screen flex relative overflow-hidden" data-testid="login-page">
      {/* Background ambience */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          background: `
            radial-gradient(800px 600px at 80% 20%, rgba(192,76,253,0.20), transparent 60%),
            radial-gradient(900px 600px at 10% 90%, rgba(255,61,138,0.18), transparent 60%)
          `,
        }}
      />
      {/* Subtle starfield */}
      <div
        className="absolute inset-0 pointer-events-none opacity-50"
        style={{
          backgroundImage:
            "radial-gradient(rgba(255,255,255,0.5) 1px, transparent 1px)," +
            "radial-gradient(rgba(255,255,255,0.3) 0.5px, transparent 0.5px)",
          backgroundSize: "120px 120px, 60px 60px",
          backgroundPosition: "0 0, 30px 30px",
          maskImage: "linear-gradient(120deg, transparent, black 30%, black 70%, transparent)",
        }}
      />

      <div className="relative z-10 w-full md:w-1/2 flex flex-col items-center justify-center px-8 py-16">
        <div className="w-full max-w-sm">
          <img src={LOGO_URL} alt="ProCloud" className="h-20 mb-12 brightness-0 invert" />
          <div className="eyebrow-accent mb-3">Internal Platform · v1</div>
          <h1 className="font-display text-5xl font-black tracking-tight mb-3" style={{ color: "var(--text-primary)" }}>
            Quote <span className="text-gradient">Engine</span>
          </h1>
          <p className="text-sm mb-10 leading-relaxed" style={{ color: "var(--text-muted)" }}>
            Branded quoting, pricing, and revision tracking — built for ProCloud Creative.
          </p>

          <form onSubmit={onSubmit} className="space-y-5">
            <div>
              <label className="label-eyebrow block mb-2">Email</label>
              <input
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                data-testid="login-email-input"
                className="input"
                placeholder="you@procloud.com.au"
                autoFocus
              />
            </div>
            <div>
              <label className="label-eyebrow block mb-2">Password</label>
              <input
                type="password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                data-testid="login-password-input"
                className="input"
                placeholder="••••••••"
              />
            </div>

            {error && (
              <div
                className="text-sm px-3 py-2 rounded"
                style={{
                  background: "var(--danger-soft)",
                  color: "#FECDD3",
                  border: "1px solid rgba(251,113,133,0.30)",
                }}
                data-testid="login-error"
              >
                {error}
              </div>
            )}

            <button
              type="submit"
              disabled={submitting}
              data-testid="login-submit-btn"
              className="btn btn-primary w-full"
              style={{ padding: "0.85rem 1.25rem" }}
            >
              {submitting ? "Signing in…" : "Sign In"}
            </button>
          </form>

          <div className="mt-12 text-xs font-mono tracking-widest" style={{ color: "var(--text-faint)" }}>
            PROCLOUD CREATIVE · MISSION CONTROL
          </div>
        </div>
      </div>

      {/* Right marketing panel */}
      <div className="hidden md:flex relative z-10 w-1/2 flex-col justify-end p-16">
        <div className="eyebrow-accent mb-4">Built For The Shop Floor</div>
        <h2
          className="font-display text-5xl font-bold tracking-tight max-w-xl leading-[1.05] mb-6"
          style={{ color: "var(--text-primary)" }}
        >
          Replace the spreadsheet.<br />
          Quote in minutes,<br />
          <span className="text-gradient">not hours.</span>
        </h2>
        <div className="text-sm max-w-md" style={{ color: "var(--text-muted)" }}>
          Structured pricing logic, one-click variations, branded PDFs.
          Internal quoting platform for ProCloud Creative.
        </div>
      </div>
    </div>
  );
}
